import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { DataTable } from '../components/DataTable'
import { cn } from '../../../lib/utils'

const useUsers = () =>
  useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, display_name, member_color, is_super_admin, is_suspended, created_at, subscriptions!subscriptions_user_id_profiles_fkey(plan, billing_cycle, status, is_comped)')
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

const PlanBadge = ({ plan, billing_cycle, status, is_comped }) => {
  if (is_comped) return <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-100 text-violet-700">Comped</span>
  if (plan === 'paid') {
    const label = status === 'trialing' ? 'Trial' : billing_cycle ? `Paid (${billing_cycle})` : 'Paid'
    return <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">{label}</span>
  }
  return <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-background text-text-muted border border-border">Free</span>
}

const STATUS_OPTIONS = ['all', 'paid', 'free', 'comped', 'suspended']

export const UsersPage = () => {
  const navigate = useNavigate()
  const { data: users = [], isLoading } = useUsers()

  const [search, setSearch]         = useState('')
  const [statusFilter, setStatus]   = useState('all')

  const filtered = users.filter((u) => {
    const q = search.toLowerCase()
    const matchSearch = !q || u.email?.toLowerCase().includes(q) || u.display_name?.toLowerCase().includes(q)
    const sub = u.subscriptions?.[0]
    const matchStatus =
      statusFilter === 'all'       ? true :
      statusFilter === 'paid'      ? sub?.plan === 'paid' && !sub?.is_comped :
      statusFilter === 'free'      ? (!sub || sub.plan === 'free') :
      statusFilter === 'comped'    ? sub?.is_comped :
      statusFilter === 'suspended' ? u.is_suspended :
      true

    return matchSearch && matchStatus
  })

  const columns = [
    {
      key: 'email', label: 'Email', sortable: true,
      render: (v, row) => (
        <span className={cn('font-medium', row.is_suspended && 'text-rose-500 line-through')}>
          {v ?? '—'}
        </span>
      ),
    },
    { key: 'display_name', label: 'Name', sortable: true },
    {
      key: 'plan', label: 'Plan',
      render: (_, row) => {
        const sub = row.subscriptions?.[0]
        return <PlanBadge plan={sub?.plan} billing_cycle={sub?.billing_cycle} status={sub?.status} is_comped={sub?.is_comped} />
      },
    },
    {
      key: 'is_suspended', label: 'Status',
      render: (v) => v
        ? <span className="text-[11px] font-semibold text-rose-600">Suspended</span>
        : <span className="text-[11px] text-emerald-600">Active</span>,
    },
    {
      key: 'created_at', label: 'Joined', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '—',
    },
  ]

  return (
    <div className="px-8 py-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Users</h1>
        <p className="text-sm text-text-muted mt-1">{users.length.toLocaleString()} total</p>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={isLoading}
        onRowClick={(row) => navigate(`/admin/users/${row.id}`)}
        emptyMessage="No users match your filters."
        actions={
          <>
            <input
              type="search"
              placeholder="Search by email or name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 max-w-sm rounded-base border border-border bg-surface px-4 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex gap-1">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-xs font-medium capitalize border transition-colors',
                    statusFilter === s
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface border-border text-text-muted hover:text-text-primary'
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </>
        }
      />
    </div>
  )
}
